import React, { useEffect, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { approximateBoundaryRing } from '../../utils/geocode';

const pinIcon = L.divIcon({
  className: 'utkarsh-map-pin',
  html: `<div style="
    width:16px;height:16px;border-radius:50%;
    background:#F9B101;border:2px solid #141414;
    box-shadow:0 1px 4px rgba(0,0,0,.35);
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const BORDER_STYLE = {
  color: '#C62828',
  weight: 3,
  dashArray: '8 6',
  fillColor: '#C62828',
  fillOpacity: 0.08,
};

/**
 * Village map — search result / click-to-pin / optional boundary.
 * Keeps Leaflet z-index below Chakra modal chrome to avoid overlay bugs.
 */
export default function VillageMap({
  latitude,
  longitude,
  boundary = null,
  clickable = false,
  onPick,
  height = '280px',
  hint,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const borderRef = useRef(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      center: [latitude || 22.839, longitude || 69.721],
      zoom: latitude != null ? 13 : 11,
      // Avoid trapping modal scroll when cursor is over the map
      scrollWheelZoom: false,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    if (clickable) {
      map.on('click', (e) => {
        onPickRef.current?.(e.latlng.lat, e.latlng.lng);
      });
    }

    mapRef.current = map;

    const invalidate = () => map.invalidateSize({ animate: false });
    const t1 = setTimeout(invalidate, 50);
    const t2 = setTimeout(invalidate, 250);
    window.addEventListener('resize', invalidate);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', invalidate);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      borderRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickable]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (borderRef.current) {
      map.removeLayer(borderRef.current);
      borderRef.current = null;
    }
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    if (latitude == null || longitude == null || Number.isNaN(Number(latitude))) {
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    markerRef.current = L.marker([lat, lng], { icon: pinIcon }).addTo(map);

    let layer = null;
    if (boundary && (boundary.type === 'Polygon' || boundary.type === 'MultiPolygon')) {
      layer = L.geoJSON(boundary, { style: () => BORDER_STYLE });
    } else {
      layer = L.polygon(approximateBoundaryRing(lat, lng), BORDER_STYLE);
    }
    layer.addTo(map);
    borderRef.current = layer;

    try {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.2));
      } else {
        map.setView([lat, lng], 13);
      }
    } catch {
      map.setView([lat, lng], 13);
    }

    const t = setTimeout(() => map.invalidateSize({ animate: false }), 80);
    return () => clearTimeout(t);
  }, [latitude, longitude, boundary]);

  return (
    <Box position="relative" zIndex={0} isolation="isolate">
      {hint ? (
        <Text fontSize="13px" color="text.secondary" mb={2}>
          {hint}
        </Text>
      ) : null}
      <Box
        ref={containerRef}
        h={height}
        w="100%"
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        overflow="hidden"
        bg="bg.input"
        position="relative"
        zIndex={0}
        sx={{
          '.leaflet-container': {
            height: '100%',
            width: '100%',
            font: 'inherit',
            zIndex: 0,
          },
          '.leaflet-pane, .leaflet-control, .leaflet-top, .leaflet-bottom': {
            zIndex: '1 !important',
          },
          '.leaflet-control-attribution': {
            fontSize: '10px',
          },
        }}
      />
      {latitude != null && longitude != null ? (
        <Text fontSize="12px" color="text.secondary" mt={2} fontFamily="mono">
          Center {Number(latitude).toFixed(5)}, {Number(longitude).toFixed(5)}
          {boundary
            ? ' · official village border'
            : ' · approx. border (no official polygon)'}
        </Text>
      ) : null}
    </Box>
  );
}
